import { useState, useRef, useCallback, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Badge,
  ProgressBar,
  ListGroup,
} from "react-bootstrap";
import {
  UploadOutlined,
  CheckCircleFilled,
  AlertFilled,
  InfoCircleOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import { getStudentRegistrationByClass } from "../../api/etudiants";
import { toast } from "sonner";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { multipleAvatarImport } from "../../api/avatar";

export interface Classe {
  id: string | number;
  nom: string;
}

interface FileStatus {
  file: File;
  matricule: string;
  valid: boolean;
  reason?: string;
  preview?: string;
}

interface BulkPhotoImportModalProps {
  show: boolean;
  onHide: () => void;
  classes: Classe[];
  // onImport: (classeId: string | number, files: { file: File; matricule: string }[]) => Promise<void>;
}

const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png"];
const MAX_FILE_SIZE_MB = 5;

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

function getMatricule(filename: string): string {
  return filename.replace(/\.[^/.]+$/, "");
}

export default function BulkPhotoImportModal({
  show,
  onHide,
  classes,
  // onImport,
}: BulkPhotoImportModalProps) {
  const { selectedYear } = useAcademicYear();
  const [selectedClasseId, setSelectedClasseId] = useState<string>("");
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDone, setImportDone] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [matricules, setMatricules] = useState<String[]>([]);
  const selectedClasse =
    classes.find((c) => String(c.id) === selectedClasseId) ?? null;

  function validateFiles(
    rawFiles: FileList | File[],
    classe: Classe | null,
  ): FileStatus[] {
    const results: FileStatus[] = [];
    Array.from(rawFiles).forEach((file) => {
      const ext = getExtension(file.name);
      const matricule = getMatricule(file.name);
      const preview = URL.createObjectURL(file);

      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        results.push({
          file,
          matricule,
          valid: false,
          reason: `Format non autorisé (.${ext})`,
          preview,
        });
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        results.push({
          file,
          matricule,
          valid: false,
          reason: `Fichier trop volumineux (max ${MAX_FILE_SIZE_MB} Mo)`,
          preview,
        });
        return;
      }
      if (!classe) {
        results.push({
          file,
          matricule,
          valid: false,
          reason: "Aucune classe sélectionnée",
          preview,
        });
        return;
      }
      if (!matricules.includes(matricule)) {
        results.push({
          file,
          matricule,
          valid: false,
          reason: `Matricule "${matricule}" introuvable dans la classe`,
          preview,
        });
        return;
      }
      results.push({ file, matricule, valid: true, preview });
    });
    return results;
  }

  function handleFiles(rawFiles: FileList | File[]) {
    if (!selectedClasseId) return;
    const statuses = validateFiles(rawFiles, selectedClasse);
    setFileStatuses((prev) => {
      const existingNames = new Set(prev.map((s) => s.file.name));
      const newStatuses = statuses.filter(
        (s) => !existingNames.has(s.file.name),
      );
      return [...prev, ...newStatuses];
    });
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) handleFiles(e.target.files);
    e.target.value = "";
  }

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    },
    [selectedClasseId, selectedClasse],
  );

  function removeFile(index: number) {
    setFileStatuses((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview ?? "");
      updated.splice(index, 1);
      return updated;
    });
  }

  function handleClasseChange(id: string) {
    setSelectedClasseId(id);
    setFileStatuses([]);
    setImportDone(false);
    setImportError(null);
  }

  async function handleImport() {
    if (!selectedYear || !selectedClasseId) return;

    const validFiles = fileStatuses.filter((s) => s.valid);
    if (!validFiles.length || !selectedClasse) return;
    setImporting(true);
    setImportProgress(0);
    setImportError(null);
    try {
      const formData = new FormData();
      formData.append("id_class", selectedClasseId);
      formData.append("id_academicYear", selectedYear?.id);

      for (let i = 0; i < validFiles.length; i++) {
        formData.append("avatars", validFiles[i].file);
        setImportProgress(Math.round(((i + 1) / validFiles.length) * 100));
      }
      await multipleAvatarImport(formData);
      setMatricules([]);
      setFileStatuses([]);
      setSelectedClasseId("");
      setImporting(false);
      setImportProgress(0);
      setImportDone(false);
      setImportError(null);
      onHide();

      toast.success("Import fait avec succes.");
    } catch (error: any) {
      // console.log("Une erreur est survenue lors de l'importation: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "une erreur inattendu c'est produite veuillez ésssyez plus tard",
      );
      setImportError("Une erreur est survenue lors de l'importation");
    } finally {
      setImporting(false);
    }
  }

  function handleClose() {
    fileStatuses.forEach((s) => s.preview && URL.revokeObjectURL(s.preview));
    setFileStatuses([]);
    setSelectedClasseId("");
    setImporting(false);
    setImportProgress(0);
    setImportDone(false);
    setImportError(null);
    onHide();
  }

  const fetchStudentData = async () => {
    try {
      if (selectedClasse?.id) {
        const response = await getStudentRegistrationByClass(
          selectedClasse?.id,
        );
        // console.log("Response student regsitration: ", response);

        const matricules = response.data?.map(
          (std: any) => std?.StudentProfil?.matricule,
        );
        setMatricules(matricules);
      }
    } catch (error: any) {
      // console.error("Erreur lors de la récupération des étudiants:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchStudentData();
  }, [selectedClasse]);
  const validCount = fileStatuses.filter((s) => s.valid).length;
  const invalidCount = fileStatuses.filter((s) => !s.valid).length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center ">
          <FileImageOutlined size={40} className="text-primary me-2" />
          Import en masse — Photos de profil
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pt-2 pb-3">
        {/* ── Instructions ── */}
        <div
          className="rounded-3 p-3 mb-4"
          style={{ background: "#f0f6ff", border: "1px solid #c5d9f7" }}
        >
          <div
            className="d-flex align-items-center gap-2 mb-2 fw-semibold"
            style={{ color: "#1a56db" }}
          >
            <InfoCircleOutlined size={16} />
            Instructions d'importation
          </div>
          <ul
            className="mb-0 ps-3 small"
            style={{ color: "#374151", lineHeight: "1.7" }}
          >
            <li>
              <strong>Nommage obligatoire :</strong> chaque photo doit porter
              exactement le <strong>matricule</strong> de l'étudiant comme nom
              de fichier (ex.&nbsp;<code>UIE-2023-003.jpg</code>).
            </li>
            <li>
              <strong>Formats acceptés :</strong> <code>.jpg</code>,{" "}
              <code>.jpeg</code>, <code>.png</code>
            </li>
            <li>
              <strong>Taille maximale :</strong> {MAX_FILE_SIZE_MB} Mo par
              fichier.
            </li>
            <li>
              Sélectionnez d'abord la <strong>classe cible</strong> — seuls les
              matricules appartenant à cette classe seront acceptés.
            </li>
            <li>
              Les fichiers dont le nom ne correspond à aucun matricule de la
              classe seront <strong>rejetés</strong>.
            </li>
          </ul>
        </div>

        {/* ── Classe selector ── */}
        <Form.Group className="mb-4">
          <Form.Label
            className="fw-semibold small text-uppercase text-dark mb-1"
            style={{ letterSpacing: "0.04em" }}
          >
            Classe cible
          </Form.Label>
          <Form.Select
            value={selectedClasseId}
            onChange={(e) => handleClasseChange(e.target.value)}
            disabled={importing}
            style={{ maxWidth: 360 }}
          >
            <option value="">-- Sélectionner une classe --</option>
            {classes.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.nom}
              </option>
            ))}
          </Form.Select>
          {selectedClasse && (
            <Form.Text className="text-muted">
              {matricules.length} étudiant(s) dans cette classe
            </Form.Text>
          )}
        </Form.Group>

        {/* ── Drop zone ── */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (selectedClasseId) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => selectedClasseId && fileInputRef.current?.click()}
          className="rounded-3 d-flex flex-column align-items-center justify-content-center gap-2 mb-3"
          style={{
            border: `2px dashed ${isDragging ? "#1a56db" : selectedClasseId ? "#9ca3af" : "#d1d5db"}`,
            background: isDragging ? "#f0f6ff" : "#f9fafb",
            minHeight: 130,
            cursor: selectedClasseId ? "pointer" : "not-allowed",
            transition: "border-color 0.2s, background 0.2s",
            opacity: selectedClasseId ? 1 : 0.5,
          }}
        >
          <UploadOutlined
            size={28}
            color={isDragging ? "#1a56db" : "#6b7280"}
          />
          <span className="fw-medium text-dark small">
            {selectedClasseId
              ? "Glissez-déposez vos photos ici, ou cliquez pour parcourir"
              : "Sélectionnez une classe pour activer l'import"}
          </span>
          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
            JPG, JPEG, PNG — max {MAX_FILE_SIZE_MB} Mo
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            multiple
            className="d-none"
            onChange={handleFileInput}
          />
        </div>

        {/* ── Summary badges ── */}
        {fileStatuses.length > 0 && (
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            <span className="small text-muted">
              {fileStatuses.length} fichier(s) ajouté(s)
            </span>
            {validCount > 0 && (
              <Badge bg="success">
                {validCount} valide{validCount > 1 ? "s" : ""}
              </Badge>
            )}
            {invalidCount > 0 && (
              <Badge bg="danger">
                {invalidCount} rejeté{invalidCount > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        )}

        {/* ── File list ── */}
        {fileStatuses.length > 0 && (
          <div
            className="rounded-3 mb-3"
            style={{
              maxHeight: "260px",
              overflowY: "auto",
              overflowX: "hidden",
              border: "1px solid #e5e7eb",
            }}
          >
            <ListGroup variant="flush">
              {fileStatuses.map((status, i) => (
                <ListGroup.Item
                  key={i}
                  className="d-flex align-items-center gap-3 px-3 py-2"
                  style={{
                    background: status.valid ? "#f0fdf4" : "#fef2f2",
                  }}
                >
                  {status.preview ? (
                    <img
                      src={status.preview}
                      alt={status.matricule}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 6,
                        background: "#e5e7eb",
                        flexShrink: 0,
                      }}
                    />
                  )}

                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-medium small text-truncate">
                      {status.file.name}
                    </div>

                    {status.reason && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.72rem" }}
                      >
                        {status.reason}
                      </div>
                    )}

                    {status.valid && (
                      <div
                        className="text-success"
                        style={{ fontSize: "0.72rem" }}
                      >
                        Matricule : {status.matricule}
                      </div>
                    )}
                  </div>

                  {status.valid ? (
                    <CheckCircleFilled
                      size={16}
                      className="text-success flex-shrink-0"
                    />
                  ) : (
                    <AlertFilled
                      size={16}
                      className="text-danger flex-shrink-0"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    disabled={importing}
                    className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 24,
                      height: 24,
                      color: "#6b7280",
                    }}
                  >
                    X
                  </button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}

        {/* ── Progress ── */}
        {importing && (
          <ProgressBar
            now={importProgress}
            label={`${importProgress}%`}
            animated
            striped
            className="mb-3"
            style={{ height: 10, borderRadius: 8 }}
          />
        )}

        {/* ── Feedback alerts ── */}
        {importDone && (
          <Alert
            variant="success"
            className="mb-0 py-2 px-3 small d-flex align-items-center gap-2"
          >
            <CheckCircleFilled size={15} />
            {validCount} photo(s) importée(s) avec succès.
          </Alert>
        )}
        {importError && (
          <Alert
            variant="danger"
            className="mb-0 py-2 px-3 small d-flex align-items-center gap-2"
          >
            <AlertFilled size={15} />
            {importError}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 px-4 pb-3 gap-2">
        <Button
          variant="outline-danger"
          onClick={handleClose}
          disabled={importing}
        >
          Annuler
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={importing || validCount === 0 || importDone}
          className="d-flex align-items-center gap-2"
        >
          <UploadOutlined size={15} />
          {importing
            ? "Importation…"
            : `Importer ${validCount > 0 ? `(${validCount})` : ""}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
